/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import './ScanPayPromo.scss';

import * as React from 'react';
import cx from 'classnames';
import {
    Icon,
    ImageWithFallback,
    useOffsetSize,
    GAPromotionViewHelper,
    gaGeneratePromotionClickEvent,
    gaPromotionObject,
    gaReportEvent,
} from 'sarsaparilla';

import scanPayIconWebp from './images/scan-pay-icon.webp';
import scanPayIconJpg from './images/scan-pay-icon.jpg';
import mobileAppPng from './images/iphone_mobile-app.png';
import mobileAppWebp from './images/iphone_mobile-app.webp';

interface ScanPayProps {
    /** Aria-Label for the promo */
    ariaLabel?: string;
    /** Type of inventory the promo is for */
    inventoryType?: string;
    /** Bold copy displayed directly before the arrow */
    buttonCopy?: React.ReactNode;
    /** Url for the promo */
    href?: string;
    /** Show or hide the arrow */
    showArrow?: boolean;
    /** Add and additional class to the existing classes */
    className?: string;
}

export function ScanPayPromo({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ariaLabel = 'Scan & Pay is available at this location',
    inventoryType = 'campground',
    buttonCopy = <>Learn&nbsp;More</>,
    href,
    showArrow = true,
    className,
}: ScanPayProps) {
    const targetRef = React.useRef<HTMLDivElement>(null);
    const { width } = useOffsetSize(targetRef);
    const pathName = window.location.pathname;
    const arrow = (
        <span className="site-kit-scan-pay-promo-icon-wrap">
            <Icon iconName="arrow-forward" />
        </span>
    );

    let screenReaderText = `This ${inventoryType} uses Scan & Pay to accept on-site payment through the ${process.env.SITE_NAME} mobile app. Download the app ahead of your visit to arrive ready.`;
    if (href) {
        screenReaderText = `${screenReaderText} Visit ${href} to learn more.`;
    }

    const promoButtonCopy = () => {
        return buttonCopy ? (
            <span className="site-kit-scan-pay-promo-button-text">
                {buttonCopy}
                {arrow}
            </span>
        ) : (
            <span className="site-kit-scan-pay-promo-button-text">
                {showArrow ? arrow : null}
            </span>
        );
    };

    const handleRedirect = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();

        gaReportEvent({
            event: gaGeneratePromotionClickEvent({
                promotion_name: 'Scan & Pay Promotion',
                promotions: [
                    gaPromotionObject({
                        name: 'Scan & Pay Promotion',
                        creative: 'ScanPayPromotion',
                        position: '0',
                    }),
                ],
            }),
        });

        Object.assign(document.createElement('a'), {
            href,
            target: '_blank',
            rel: 'noopener noreferrer',
        }).click();
    };

    const scanPayBody = () => {
        return (
            <div className={cx('site-kit-scan-pay-promo-inner-wrap')}>
                <div className="site-kit-scan-pay-promo-text-block">
                    <ImageWithFallback
                        src={scanPayIconWebp}
                        fallbackSrc={scanPayIconJpg}
                        alt="Scan & Pay"
                        imgHeight="55"
                        imgWidth="31"
                    />
                    <p>
                        This {inventoryType} uses <i>Scan & Pay</i> to accept on-site
                        payment through the {process.env.SITE_NAME} mobile app.{' '}
                        <span className="site-kit-scan-pay-promo-download-highlight">
                            Download the app
                        </span>{' '}
                        ahead of your visit to arrive ready. {href && promoButtonCopy()}
                    </p>
                </div>
                {width > 500 && (
                    <div className="site-kit-scan-pay-promo-phone-block">
                        <ImageWithFallback
                            src={mobileAppWebp}
                            fallbackSrc={mobileAppPng}
                            alt={`Scan & Pay on ${process.env.SITE_NAME} app`}
                            imgHeight="92"
                            imgWidth="91"
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div
            ref={targetRef}
            className={cx(className, 'site-kit-scan-pay-promo-wrap')}
            aria-label={screenReaderText}
        >
            {href ? (
                <a
                    href={href}
                    onClick={handleRedirect}
                    id="ga-scan-pay-promo"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <GAPromotionViewHelper
                        name={'Scan & Pay Promotion'}
                        creative={'ScanPayPromotion'}
                        position={pathName}
                        refForIntersection={targetRef}
                    />
                    {scanPayBody()}
                </a>
            ) : (
                scanPayBody()
            )}
        </div>
    );
}

// cSpell:ignore webp
